import { create } from "zustand";
import { api } from "@/lib/api";
import type { CreateUserDto, ReadUserDto, UpdateUserDto } from "@package/types";
import type { AsyncState } from "./types";

interface UsersState extends AsyncState {
  users: ReadUserDto[];
}

interface UsersActions {
  loadUsers: () => Promise<void>;
  createUser: (data: CreateUserDto) => Promise<void>;
  updateUser: (id: number, data: UpdateUserDto) => Promise<void>;
  deleteUser: (id: number) => Promise<void>;
  reset: () => void;
}

type UsersStore = UsersState & UsersActions;

export const useUsersStore = create<UsersStore>()((set) => ({
  users: [],
  isLoading: false,
  isLoaded: false,
  error: null,

  loadUsers: async () => {
    set({ isLoading: true, error: null });
    try {
      const data = await api.get<ReadUserDto[]>("/users");
      set({ users: data, isLoading: false, isLoaded: true });
    } catch (error) {
      set({ isLoading: false, error: (error as Error).message });
    }
  },

  createUser: async (data: CreateUserDto) => {
    try {
      const user = await api.post<ReadUserDto>("/users", data);
      set((state) => ({ users: [user, ...state.users] }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  updateUser: async (id: number, data: UpdateUserDto) => {
    try {
      const updated = await api.patch<ReadUserDto>(`/users/${id}`, data);
      set((state) => ({
        users: state.users.map((u) => (u.id === id ? updated : u))
      }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  deleteUser: async (id: number) => {
    try {
      await api.delete(`/users/${id}`);
      set((state) => ({ users: state.users.filter((u) => u.id !== id) }));
    } catch (error) {
      set({ error: (error as Error).message });
      throw error;
    }
  },

  reset: () =>
    set({ users: [], isLoading: false, isLoaded: false, error: null })
}));
